import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, Button, useTheme, keyframes } from "@mui/material";
import { Link } from "react-router-dom";
import videoBg from "../assets/Namami.mp4";

// === Animations ===
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scrollAnim = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); opacity: 1; }
  40% { transform: translateY(8px); opacity: 0.6; }
  60% { transform: translateY(4px); opacity: 0.8; }
`;

export default function Hero() {
  const theme = useTheme();
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const fadeRef = useRef(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const canvasRef = useRef(null);

  // === Canvas Star Animation ===
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    let stars = [];
    let animationFrame;

    const initStars = () => {
      const w = (canvas.width = window.innerWidth);
      const h = (canvas.height = window.innerHeight);
      stars = Array.from({ length: 500 }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        z: Math.random() * w,
        size: Math.random() * 3 + 0.2,
        speed: Math.random() * 1 + 0.05,
      }));
    };

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.fillStyle = "rgba(0, 10, 40, 0.8)";
      ctx.fillRect(0, 0, w, h);

      ctx.fillStyle = "rgba(255,255,255,0.9)";
      stars.forEach((star) => {
        star.z -= star.speed;
        if (star.z <= 0) star.z = w;
        const k = 128.0 / star.z;
        const px = star.x * k + w / 2;
        const py = star.y * k + h / 2;
        if (px >= 0 && px <= w && py >= 0 && py <= h) {
          const size = star.size * (1 - star.z / w);
          ctx.beginPath();
          ctx.arc(px, py, size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,255,${0.6 + Math.random() * 0.4})`;
          ctx.fill();
        }
      });
      animationFrame = requestAnimationFrame(draw);
    };

    initStars();
    draw();
    window.addEventListener("resize", initStars);
    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", initStars);
    };
  }, []);

  // === Video Fade Logic ===
  useEffect(() => {
    const video = videoRef.current;
    const overlay = overlayRef.current;
    if (!video || !overlay) return;

    const handleCanPlay = () => setVideoLoaded(true);

    const handleTimeUpdate = () => {
      if (video.duration && video.currentTime > video.duration - 1.5 && !fadeRef.current) {
        fadeRef.current = true;
        overlay.style.transition = "opacity 1s ease";
        overlay.style.opacity = "1";
        video.style.transition = "opacity 1.2s ease";
        video.style.opacity = "0.4";

        setTimeout(() => {
          video.currentTime = 0;
          video.play();
          video.style.opacity = "1";
          overlay.style.opacity = "0";
          fadeRef.current = false;
        }, 900);
      }
    };

    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => {
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, []);

  return (
    <Box
      sx={{
        position: "relative",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontFamily: "'Poppins', 'Roboto', sans-serif",
      }}
    >
      {/* === Animated Milky Way Canvas Placeholder === */}
      {!videoLoaded && (
        <canvas
          ref={canvasRef}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: -3,
            background:
              "radial-gradient(circle at 50% 50%, rgba(0, 0, 0, 0.9), rgba(6, 6, 31, 1))",
            transition: "opacity 1s ease",
          }}
        />
      )}

      {/* === Video Background === */}
      <Box
        component="video"
        ref={videoRef}
        src={videoBg}
        autoPlay
        muted
        playsInline
        onCanPlay={() => setVideoLoaded(true)}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: -3,
          opacity: videoLoaded ? 1 : 0,
          transition: "opacity 2s ease",
        }}
      />

      {/* === Deep Blue Overlay Transition === */}
      <Box
        ref={overlayRef}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "linear-gradient(135deg, rgba(0, 36, 86, 0.8), rgba(0, 0, 0, 0.9))",
          zIndex: -2,
          opacity: 0,
          transition: "opacity 1s ease",
        }}
      />

      {/* === Subtle Dark Overlay === */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.8) 100%)",
          zIndex: -1,
        }}
      />

      {/* === Hero Content === */}
      <Box
        sx={{
          textAlign: "center",
          px: 3,
          maxWidth: "900px",
          animation: `${fadeIn} 1.5s ease both`,
        }}
      >
        <Typography
          variant="h3"
          fontWeight={800}
          sx={{
            mb: 2,
            letterSpacing: "0.5px",
            [theme.breakpoints.down("sm")]: { fontSize: "1.9rem" },
          }}
        >
          Empowering Facilities, Building Trust.
        </Typography>

        <Typography
          variant="h6"
          color="rgba(255,255,255,0.9)"
          sx={{
            mb: 4,
            maxWidth: "700px",
            mx: "auto",
            fontWeight: 400,
            lineHeight: 1.6,
          }}
        >
          Integrated Facility Management and Support Services across India —
          blending technology, professionalism, and people to redefine quality.
        </Typography>

        <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to="/contact"
            sx={{
              px: 4,
              py: 1.4,
              fontWeight: 600,
              borderRadius: 3,
              textTransform: "none",
              background: "linear-gradient(90deg, #004aad, #002f6c)",
              "&:hover": {
                background: "linear-gradient(90deg, #005be3, #004aad)",
              },
            }}
          >
            Get in Touch
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            component={Link}
            to="/services"
            sx={{
              px: 4,
              py: 1.4,
              fontWeight: 600,
              borderRadius: 3,
              textTransform: "none",
              borderColor: "rgba(255,255,255,0.6)",
              "&:hover": {
                borderColor: "#fff",
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            Explore Services
          </Button>
        </Box>
      </Box>

      {/* === Scroll Down Indicator === */}
      <Box
        sx={{
          position: "absolute",
          bottom: 40,
          left: "50%",
          transform: "translateX(-50%)",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 25,
            height: 40,
            border: "2px solid rgba(255,255,255,0.8)",
            borderRadius: "20px",
            position: "relative",
            mx: "auto",
            "&::after": {
              content: '""',
              position: "absolute",
              top: 8,
              left: "50%",
              transform: "translateX(-50%)",
              width: 6,
              height: 6,
              backgroundColor: "#fff",
              borderRadius: "50%",
              animation: `${scrollAnim} 1.6s infinite`,
            },
          }}
        />
        <Typography
          variant="caption"
          color="rgba(255,255,255,0.8)"
          mt={1}
          sx={{ letterSpacing: "1px" }}
        >
          Scroll Down
        </Typography>
      </Box>
    </Box>
  );
}
